import { ListingOfferCardSmall } from 'app/components/listing-offer-card.small';
import { isLoggedInAtom } from 'app/atoms';
import { Column } from 'app/design/primitives/box';
import { useListingsByCategories } from 'app/network/listing';
import { useLandingDiscoveryFeatures } from 'app/hooks/use-landing-discovery-features';
import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { HorizontalListingsSkeleton } from '../../all-listings/components/horizontal-listings-skeleton';
import { ListingListWithStrategy } from '../../all-listings/components/recommended-listings';
import { Strategies } from '../../all-listings/components/recommended-listings.utils';
import { DesktopRecommendationSection } from '../../all-listings/components/recommended-listings.large';

export function useLandingCategoryOffers(enabled = true) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { showLandingCategories } = useLandingDiscoveryFeatures();
  const canLoad = enabled && isLoggedIn && showLandingCategories;
  const query = useListingsByCategories({ enabled: canLoad });
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchNextPageError } = query;
  const lastPage = data?.pages[data.pages.length - 1];

  // Empty category batches still advance pagination. Otherwise a sparse catalog
  // can stop before reaching its first populated category.
  useEffect(() => {
    if (canLoad && lastPage?.results.length === 0 && hasNextPage && !isFetching && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [canLoad, fetchNextPage, hasNextPage, isFetching, isFetchNextPageError, lastPage]);

  const loadMore = () => {
    if (canLoad && hasNextPage && !isFetching && !isFetchNextPageError) {
      void fetchNextPage();
    }
  };

  return { ...query, enabled: canLoad, loadMore };
}

export function LandingCategoryOffers({
  query,
  onCategoryPress,
  variant,
}: {
  query: ReturnType<typeof useLandingCategoryOffers>;
  onCategoryPress: (categoryId: string) => void;
  variant: 'small' | 'large';
}) {
  if (!query.enabled) return null;
  if (query.isPending) return <HorizontalListingsSkeleton />;

  return (
    <Column gap="spacing.general.x6">
      {query.data?.pages.flatMap((page) =>
        page.results
          .filter((result) => result.listings.length > 0)
          .map(({ categoryId, listings }) => {
            const props = {
              strategyProps: { strategy: Strategies.ByCategory },
              recommendationState: { recommendations: listings, isPending: false, show: true },
              recommendations: listings,
              titleSuffix: page.categories.find((category) => category.id === categoryId)?.name,
              onViewAll: () => onCategoryPress(categoryId),
            };
            return variant === 'large' ? (
              <DesktopRecommendationSection horizontalPadding={0} key={categoryId} {...props} />
            ) : (
              <ListingListWithStrategy key={categoryId} {...props} card={ListingOfferCardSmall} />
            );
          }),
      )}
      {query.isFetchingNextPage ? <HorizontalListingsSkeleton /> : null}
    </Column>
  );
}
